import React, { useState } from "react";
import pluralize from "pluralize";
import { TitleIcon } from "@/features/shell/components/shared/TitleIcon";
import { useTitle } from "hooks/graphql/useTitles";
import { triggerHaptic } from "@/utilities/haptics";

import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { useSetSingleStake } from "@/features/betslip/hooks/useStakeActions";
import { useRemoveSelection } from "@/features/betslip/hooks/useBetslipActions";
import { useRemainingPromoBalance } from "@/features/betslip/hooks/useRemainingPromoBalance";
import type { ExoticSelection } from "@/features/betslip/types";
import { BetCard } from "@/features/betslip/components/cards/BetCard";
import { SelectionAlert } from "@/features/betslip/components/parts/SelectionAlert";
import { SelectionEventStatus } from "@/features/betslip/components/parts/SelectionEventStatus";
import { BetCardHeader } from "@/features/betslip/components/parts/BetCardHeader";
import { RemoveButton } from "@/features/betslip/components/parts/RemoveButton";
import { StakeInput } from "@/features/betslip/components/parts/StakeInput";
import { ComboQuickStakeChips } from "@/features/betslip/components/parts/ComboQuickStakeChips";
import { BetRow } from "@/features/betslip/components/parts/BetRow";
import { BetSpine } from "@/features/betslip/components/parts/BetSpine";
import { ComboGroup } from "@/features/betslip/components/parts/ComboGroup";
import { PlaceMedal } from "@/features/betslip/components/parts/PlaceMedal";
import { PayoutValue } from "@/features/betslip/components/parts/PayoutValue";
import { StakeStrip } from "@/features/betslip/components/parts/StakeStrip";
import { PromoPills } from "@/features/betslip/components/parts/tokens/PromoPills";
import { safeExoticStake } from "@/utilities/exoticStake";
import { convertAmountToCents, convertCentsToAmount } from "common/currency";

type ExoticSelectionCardProps = {
  outcomeId: string;
  selection: ExoticSelection;
};

const snapExoticCents = (stakeCents: number, combinations: number): number => {
  if (combinations <= 0) return stakeCents;
  const dollars = convertCentsToAmount(stakeCents);
  const safeDollars = safeExoticStake(dollars, combinations);
  return convertAmountToCents(safeDollars);
};

// The exotic's market type picks its ComboGroup tag; anything unexpected falls
// back to the generic exotic tag.
const exoticVariant = (
  marketType: ExoticSelection["marketType"],
): "quinella" | "exacta" | "trifecta" | "first4" | "exotic" => {
  switch (marketType) {
    case "QUINELLA":
      return "quinella";
    case "EXACTA":
      return "exacta";
    case "TRIFECTA":
      return "trifecta";
    case "FIRST_4":
      return "first4";
    default:
      return "exotic";
  }
};

export const ExoticSelectionCard = ({
  outcomeId,
  selection,
}: ExoticSelectionCardProps) => {
  const title = useTitle((selection.sport ?? "").toLowerCase());
  const stake = useBetslipStore((s) => s.stakes.singles[outcomeId] ?? 0);
  const isPlacing = useBetslipStore((s) => s.isPlacing);
  const isStakeDisabled = useBetslipStore(
    (s) => s.stakeDisabled[outcomeId] ?? false,
  );
  const isPromo = useBetslipStore(
    (s) => s.promoCash.singles[outcomeId] ?? false,
  );
  const setSingleStake = useSetSingleStake();
  const removeSelection = useRemoveSelection();
  const maxPromoStakeCents = useRemainingPromoBalance({
    type: "single",
    outcomeId,
  });
  const [activeInput, setActiveInput] = useState<"total" | "perCombo" | null>(
    null,
  );
  const hasFocus = activeInput !== null;
  const combinations = selection.combinations ?? 1;
  const perComboStake = combinations > 0 ? stake / combinations : 0;

  const handleTotalChange = (cents: number) => {
    setSingleStake(outcomeId, cents);
  };
  const handlePerComboChange = (cents: number) => {
    setSingleStake(outcomeId, cents * combinations);
  };
  const handleSnapOnBlur = () => {
    setSingleStake(outcomeId, snapExoticCents(stake, combinations));
    setActiveInput(null);
  };
  const handleRemove = () => {
    triggerHaptic.click();
    removeSelection(outcomeId);
  };

  return (
    <BetCard
      testId={`selection-${outcomeId}`}
      alert={<SelectionAlert outcomeId={outcomeId} placement="card" />}
    >
      <BetCardHeader
        icon={<TitleIcon title={title} />}
        title={selection.eventName}
        clamp={<SelectionEventStatus selection={selection} />}
        rightSlot={
          <RemoveButton
            onClick={handleRemove}
            disabled={isPlacing}
            testId={`remove-${outcomeId}`}
          />
        }
        testId={`header-exotic-${outcomeId}`}
      />
      <SelectionAlert outcomeId={outcomeId} placement="outcome" />
      <ComboGroup
        variant={exoticVariant(selection.marketType)}
        label={pluralize("Combo", selection.combinations ?? 0, true)}
        testId={`exotic-combos-${outcomeId}`}
      >
        <BetSpine padded>
          {selection.positions.map((runners, index) => (
            <BetRow
              key={`position-${index}`}
              icon={<PlaceMedal place={index + 1} />}
              name={runners
                .map((runner) => runner.runnerNumber || runner.outcomeName)
                .join(", ")}
              testId={`exotic-position-${outcomeId}-${index + 1}`}
            />
          ))}
        </BetSpine>
      </ComboGroup>
      <StakeStrip
        testId={`exotic-stake-strip-${outcomeId}`}
        promoActive={isPromo}
        labels={["Total Stake", "Stake/Combo", "Est. Payout"]}
        cellVariants={["field", "field", "plain"]}
        leftSlot={
          <StakeInput
            value={stake}
            onChange={handleTotalChange}
            disabled={isStakeDisabled}
            testId={`stake-input-${outcomeId}`}
            onFocus={() => setActiveInput("total")}
            onBlur={handleSnapOnBlur}
          />
        }
        middleSlot={
          <StakeInput
            value={perComboStake}
            onChange={handlePerComboChange}
            disabled={isStakeDisabled}
            testId={`stake-input-per-combo-${outcomeId}`}
            onFocus={() => setActiveInput("perCombo")}
            onBlur={handleSnapOnBlur}
          />
        }
        rightSlot={<PayoutValue dollars={0} tbd testId="exotic-payout" />}
        quickStake={
          <ComboQuickStakeChips
            isOpen={hasFocus && !isStakeDisabled}
            activeInput={activeInput}
            totalCents={stake}
            perComboCents={perComboStake}
            onSetTotalCents={handleTotalChange}
            onSetPerComboCents={handlePerComboChange}
            isPromo={isPromo}
            maxPromoStakeCents={maxPromoStakeCents}
          />
        }
      />
      <PromoPills
        outcomeIds={[outcomeId]}
        isMulti={false}
        selection={selection}
      />
    </BetCard>
  );
};
